import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

interface SubscriptionPlanDialogProps {
  open: boolean;
  onClose: () => void;
  onSubscribe: (plan: 'basic' | 'enterprise') => void;
  userHasActiveSubscription: boolean;
  onOpenCancelDialog: () => void;
}

const SubscriptionPlanDialog: React.FC<SubscriptionPlanDialogProps> = ({
  open,
  onClose,
  onSubscribe,
  userHasActiveSubscription,
  onOpenCancelDialog
}) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>Choose a Subscription Plan</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : undefined
              }}>
                <Typography variant="h5">Basic Plan</Typography>
                <Typography variant="h4" sx={{ mt: 2 }}>SGD 27/month</Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ minHeight: 150 }}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="1 Chatbot" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Basic Support" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Standard Features" />
                    </ListItem>
                  </List>
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  fullWidth
                  variant="contained"
                  onClick={() => onSubscribe('basic')}
                >
                  Select Basic Plan
                </Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              border: '2px solid #1976d2',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : undefined
            }}>
              <CardContent>
                <Typography variant="h5">Enterprise Plan</Typography>
                <Typography variant="h4" sx={{ mt: 2 }}>SGD 54/month</Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ minHeight: 150 }}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="3 Chatbots" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Priority Support" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="All Advanced Features" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="AI Responses" />
                    </ListItem>
                  </List>
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => onSubscribe('enterprise')}
                >
                  Select Enterprise Plan
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {userHasActiveSubscription && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              onClick={onOpenCancelDialog}
              sx={{ textTransform: 'none' }}
            >
              Cancel Current Subscription
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionPlanDialog; 